/**
 * Client-side FELT_USER_TOKENS cookie helpers.
 *
 * NOTE: This file uses `document.cookie` and is client-only despite living
 * in the edge/ directory. The edge/ directory groups all token-related
 * code; the cookie name constant is also consumed by middleware (edge-safe).
 *
 * SECURITY: The FELT_USER_TOKENS cookie stores session tokens (e.g.
 * sessionAuthToken for Kinsa) and is NOT HttpOnly — it must be readable
 * by client-side JavaScript for the token sync flow. Ensure strong CSP
 * headers are in place to mitigate XSS token exfiltration risk.
 */

import type { ExternalAccountID } from "@repo/db/branded-types";
import type { DefaultCustomData } from "@repo/sdk";

export const FELT_USER_TOKENS_COOKIE_NAME = "FELT_USER_TOKENS";

/**
 * Reads the `iat` (issued-at) claim from a JWT without verifying its
 * signature. Returns null for anything that isn't a well-formed JWT with a
 * numeric `iat`. Used only to compare relative freshness of two idTokens.
 */
function readJwtIssuedAt(token: unknown): number | null {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded)) as { iat?: unknown };
    return typeof payload.iat === "number" ? payload.iat : null;
  } catch {
    return null;
  }
}

export function setUserTokensCookie(data: Record<string, unknown>): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  const value = encodeURIComponent(JSON.stringify(data));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${FELT_USER_TOKENS_COOKIE_NAME}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${secure}`;
}

/**
 * Picks `tokenKeys` from account.custom, adds externalAccountId,
 * and sets the FELT_USER_TOKENS cookie if it changed. The caller is
 * responsible for passing the brand-appropriate key list (Kinsa vs Owlet)
 * so this file stays brand-agnostic.
 * @returns true if the cookie was set or already current, false if no account data available.
 */
export function syncUserTokensCookie(
  accountId: ExternalAccountID,
  custom: DefaultCustomData | undefined,
  tokenKeys: readonly string[]
): boolean {
  const picked: Record<string, unknown> = {};
  picked.externalAccountId = accountId;
  if (custom) {
    for (const key of tokenKeys) {
      if (key in custom) {
        picked[key] = (custom as Record<string, unknown>)[key];
      }
    }
  }

  // Skip the cookie write if the existing value is identical to avoid
  // unnecessary Set-Cookie headers and cookie churn.
  const existing = getUserTokensCookie();
  if (existing && JSON.stringify(existing) === JSON.stringify(picked)) {
    return true;
  }

  // Never clobber a newer idToken. On Owlet, middleware rotates the
  // FELT_USER_TOKENS cookie (minting a fresh idToken) whenever verify returns
  // 401, but the SDK initial context still holds the launch-time idToken. Left
  // unguarded, this observer would overwrite the rotated token with the stale
  // one on the next render, so the following request verifies as expired and
  // rotates again — a self-sustaining verify→refresh→rotate loop that never
  // quiesces. Only skip when the cookie's idToken is strictly newer, so a
  // genuinely fresh token re-injected by the native shell (re-auth) is still
  // adopted. Non-JWT tokens (e.g. Kinsa) return null iat and fall through to
  // the normal write.
  //
  // The freshness skip is scoped to the *same* account: if the existing cookie
  // belongs to a different account (account switch), an older-looking incoming
  // idToken must still write through so the new account's full token set
  // (idToken + refreshToken + externalAccountId) lands — otherwise we'd strand
  // the previous account's tokens under the new identity.
  const sameAccount = existing?.["externalAccountId"] === accountId;
  const existingIat = readJwtIssuedAt(existing?.["idToken"]);
  const incomingIat = readJwtIssuedAt(picked["idToken"]);
  if (
    sameAccount &&
    existingIat != null &&
    incomingIat != null &&
    existingIat > incomingIat
  ) {
    return true;
  }

  setUserTokensCookie(picked);
  return true;
}

export function getUserTokensCookie(): Record<string, unknown> | null {
  if (typeof document === "undefined") {
    return null;
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${FELT_USER_TOKENS_COOKIE_NAME}=([^;]*)`)
  );
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}
